import React, { useState, useEffect, useRef } from "react";
import moment from "moment";

import { IDog, IDogRunSummary, ISportProgress, IUKCRallyProgress } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import { Modal, useModal } from "../../../components/Modal/Modal";
import ManualRunEntryModal from "../ManualRunEntry/ManualRunEntryModal";
import ContextMenu, { IContextMenuOption } from "../../../components/ContextMenu/ContextMenu";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";

import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";

import "./UKCRallyCard.scss";

interface IProps {
  dog: IDog;
  ukcRallyData: IUKCRallyProgress;
  onRunUpdated?: () => void;
}

const UKCRallyCard: React.FC<IProps> = ({ dog, ukcRallyData, onRunUpdated }) => {
  const runHistoryModal = useModal();
  const manualRunModal = useModal();
  const deleteConfirmModal = useModal();
  const [editingRun, setEditingRun] = useState<IDogRunSummary | null>(null);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [runToDelete, setRunToDelete] = useState<string | null>(null);
  const [localRuns, setLocalRuns] = useState<IDogRunSummary[]>(ukcRallyData.recent_runs || []);
  
  // Track if we need to refresh parent when modal closes
  const needsParentRefreshRef = useRef(false);
  
  // Sync localRuns with ukcRallyData when it changes
  useEffect(() => {
    setLocalRuns(ukcRallyData.recent_runs || []);
  }, [ukcRallyData.recent_runs]);

  const handleAddRun = () => {
    setEditingRun(null);
    manualRunModal.toggle();
  };

  const handleEditRun = (run: IDogRunSummary) => {
    setEditingRun(run);
    manualRunModal.toggle();
  };

  const handleRunSaved = async () => {
    manualRunModal.toggle();
    
    // Fetch fresh run data to update the table without closing the modal
    try {
      const response = await apiService.getRibbonRoomData(dog._id);
      const ukcRallySport = response.sports.find((s: ISportProgress) => s.show_type === Constants.show_type.ukc_rally && s.sanctioning_club === Constants.sanctioning_club.UKC);
      if (ukcRallySport) {
        setLocalRuns(ukcRallySport.recent_runs || []);
      }
    } catch (error) {
      console.error('Failed to refresh runs:', error);
    }
    
    // Mark that we need to refresh parent when the run history modal closes
    needsParentRefreshRef.current = true;
  };

  const handleDeleteRun = (runId: string) => {
    setRunToDelete(runId);
    deleteConfirmModal.toggle();
  };

  // Stable refs for AG Grid cell renderer callbacks (avoids stale closure issues)
  const handleEditRunRef = useRef(handleEditRun);
  const handleDeleteRunRef = useRef(handleDeleteRun);
  const deletingRunIdRef = useRef(deletingRunId);
  useEffect(() => { handleEditRunRef.current = handleEditRun; });
  useEffect(() => { handleDeleteRunRef.current = handleDeleteRun; });
  useEffect(() => { deletingRunIdRef.current = deletingRunId; }, [deletingRunId]);

  const confirmDelete = async () => {
    if (!runToDelete) return;

    setDeletingRunId(runToDelete);
    deleteConfirmModal.toggle();
    
    try {
      await apiService.deleteManualRun(runToDelete);
      toast.show({
        title: "Run Deleted",
        content: "The run has been removed and title progress has been recalculated.",
        duration: 5000,
        type: "success"
      });
      
      // Refresh the local runs list
      const response = await apiService.getRibbonRoomData(dog._id);
      const ukcRallySport = response.sports.find((s: ISportProgress) => s.show_type === Constants.show_type.ukc_rally && s.sanctioning_club === Constants.sanctioning_club.UKC);
      if (ukcRallySport) {
        setLocalRuns(ukcRallySport.recent_runs || []);
      }
      
      // Mark that parent needs refresh when modal closes
      needsParentRefreshRef.current = true;
    } catch (error: any) {
      toast.show({
        title: "Error",
        content: error.response?.data?.message || "Failed to delete run. Please try again.",
        duration: 5000,
        type: "fail"
      });
    } finally {
      setDeletingRunId(null);
      setRunToDelete(null);
    }
  };

  const handleCloseRunsModal = () => {
    runHistoryModal.toggle();
    
    // If we added/edited/deleted runs, refresh the parent to update title progress
    if (needsParentRefreshRef.current) {
      needsParentRefreshRef.current = false;
      onRunUpdated?.();
    }
  };

  const getActionMenuOptions = (run: IDogRunSummary) => {
    if (!run) return null;
    const isManualEntry = run.data_source === Constants.run_data_source.manual_entry;
    if (run.verified) return null;
    if (deletingRunIdRef.current === run._id) return null;
    if (!isManualEntry) return null;
    return [
      { label: 'Edit', onClick: () => handleEditRunRef.current(run) },
      { label: 'Delete', onClick: () => handleDeleteRunRef.current(run._id) }
    ] as IContextMenuOption[];
  };

  const ActionsCellRenderer = (params: any) => {
    const run = params.data;
    if (!run) return null;
    if (run.verified) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#10b981', fontWeight: 600 }}>Verified</span>;
    if (deletingRunIdRef.current === run._id) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Deleting...</span>;
    const options = params.getOptions(run);
    if (!options) return null;
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><ContextMenu options={options} disablePortal /></div>;
  };

  // AGGrid column definitions
  const columnDefs: ColDef[] = [
    {
      headerName: 'Date',
      field: 'trial_date',
      valueFormatter: (params) => params.value ? moment(params.value).format('M/D/YYYY') : '-',
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Class',
      field: 'class',
      valueFormatter: (params) => params.value || '-',
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Score',
      field: 'score',
      valueFormatter: (params) => params.value !== undefined ? params.value : '-',
      cellStyle: (params) => {
        if (params.value && params.value >= 70) {
          return { color: '#10b981', fontWeight: 600 };
        } else if (params.value && params.value < 70) {
          return { color: '#ef4444', fontWeight: 600 };
        }
        return undefined;
      },
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Placement',
      field: 'placement',
      valueFormatter: (params) => params.value || '-',
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Dogs Defeated',
      field: 'dogs_defeated',
      valueFormatter: (params) => params.value || '-',
      minWidth: 130,
      flex: 1
    },
    {
      headerName: 'Awards',
      field: 'awards',
      valueFormatter: (params) => params.value && params.value.length > 0 ? params.value.join(', ') : '-',
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: { getOptions: getActionMenuOptions },
      cellStyle: { overflow: 'visible', zIndex: 1 },
      minWidth: 100,
      flex: 1
    }
  ];

  // Sort levels in correct order (Level 1 -> 2 -> 3 -> Masters)
  const levelOrder = [
    Constants.dog_class_element_level.ukc_rally_level_1,
    Constants.dog_class_element_level.ukc_rally_level_2,
    Constants.dog_class_element_level.ukc_rally_level_3,
    Constants.dog_class_element_level.ukc_rally_masters
  ];

  const sortedLevels = ukcRallyData.levels.sort((a, b) => {
    return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
  });

  return (
    <div className="ukcRallyCard">
      {/* Header */}
      <div className="cardHeader">
        <div className="headerLeft">
          <h3>UKC Rally</h3>
          <p className="cardSubtitle">On & Off Leash</p>
        </div>
        <div className="headerRight">
          <div className="statsGrid">
            <div className="statItem">
              <div className="statLabel">Total Titles</div>
              <div className="statValue">{ukcRallyData.earned_titles.length}</div>
            </div>
            <div className="statItem">
              <div className="statLabel">Championship Points</div>
              <div className="statValue">{ukcRallyData.total_championship_points}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Earned Titles */}
      {ukcRallyData.earned_titles && ukcRallyData.earned_titles.length > 0 && (
        <div className="earnedTitlesSection">
          <h4>Earned Titles ({ukcRallyData.earned_titles.length})</h4>
          <div className="titleBadges">
            {ukcRallyData.earned_titles.map(title => (
              <div 
                key={title.title_code} 
                className="titleBadge"
                style={{ backgroundColor: title.color_hex || '#4CAF50' }}
              >
                <div className="badgeInfo">
                  <div className="badgeTitle">{title.title_code}</div>
                  <div className="badgeDate">{title.earned_date ? new Date(title.earned_date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress Section - Each level in its own row */}
      <div className="progressSection">
        {/* Basic Levels - Each in own row */}
        {sortedLevels.map((levelData) => {
          // Determine the display name
          const titleCode = levelData.title_code;
          const levelName = levelData.level;
          
          // Extract just the class name from the full level string (e.g., "UKC Rally Obedience 1" -> "Rally 1")
          const shortLevelName = levelName.replace('UKC ', '');
          
          return (
            <div key={levelData.level} className="levelRow">
              <div className="levelHeader">
                <h4>
                  {titleCode ? (
                    <>
                      <strong>{titleCode}</strong> ({shortLevelName})
                    </>
                  ) : (
                    levelName
                  )}
                  {levelData.title_earned && <span className="earnedCheck"> ✓</span>}
                </h4>
              </div>
              <div className="levelStats">
                <div className="statItem">
                  <span className="statLabel">{levelData.qualified_count}/{levelData.required_legs} Legs</span>
                </div>
                {levelData.highest_score && (
                  <div className="statItem">
                    <span className="statLabel">Best: {levelData.highest_score}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Championship Progress - All new titles */}
        
        {/* URX - United Rally Excellent (Double Qs) */}
        {ukcRallyData.urx && (
          <div className="levelRow multiEntryRow">
            <div className="levelHeader">
              <h4>
                <strong>{ukcRallyData.urx.title_code}</strong> (Double Qs)
                {ukcRallyData.urx.title_earned && <span className="earnedCheck"> ✓</span>}
              </h4>
              <p className="subtitle">Rally 2-B + Rally 3-B</p>
            </div>
            <div className="levelStats">
              <div className="statItem">
                <span className="statLabel">{ukcRallyData.urx.double_qs}/{ukcRallyData.urx.required_double_qs} Double Qs</span>
              </div>
            </div>
          </div>
        )}

        {/* UROC - United Rally Obedience Champion (Points) */}
        {ukcRallyData.uroc && (
          <div className="levelRow championshipRow">
            <div className="levelHeader">
              <h4>
                <strong>UROC</strong> (Championship Points)
                {ukcRallyData.uroc.title_earned && <span className="earnedCheck"> ✓</span>}
              </h4>
            </div>
            <div className="levelStats">
              <div className="statItem">
                <span className="statLabel">Total: {ukcRallyData.uroc.total_points}/{ukcRallyData.uroc.required_points}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">L2B: {ukcRallyData.uroc.level_2b_points}/{ukcRallyData.uroc.required_level_2b_points}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">L3B: {ukcRallyData.uroc.level_3b_points}/{ukcRallyData.uroc.required_level_3b_points}</span>
              </div>
            </div>
          </div>
        )}

        {/* UROG - United Rally Obedience Grand Champion */}
        {ukcRallyData.urog && (
          <div className="levelRow championshipRow">
            <div className="levelHeader">
              <h4>
                <strong>UROG</strong> (Grand Championship)
                {ukcRallyData.urog.title_earned && <span className="earnedCheck"> ✓</span>}
              </h4>
              <p className="subtitle">15 Double Qs with combined score ≥192</p>
            </div>
            <div className="levelStats">
              <div className="statItem">
                <span className="statLabel">Qualifying: {ukcRallyData.urog.qualifying_double_qs}/{ukcRallyData.urog.required_double_qs}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">Total Double Qs: {ukcRallyData.urog.double_qs}</span>
              </div>
            </div>
          </div>
        )}

        {/* RMX - Rally Master Excellent (Triple Qs) */}
        {ukcRallyData.rmx && (
          <div className="levelRow multiEntryRow">
            <div className="levelHeader">
              <h4>
                <strong>{ukcRallyData.rmx.title_code}</strong> (Triple Qs)
                {ukcRallyData.rmx.title_earned && <span className="earnedCheck"> ✓</span>}
              </h4>
              <p className="subtitle">Rally 2-B + Rally 3-B + Masters-B</p>
            </div>
            <div className="levelStats">
              <div className="statItem">
                <span className="statLabel">{ukcRallyData.rmx.triple_qs}/{ukcRallyData.rmx.required_triple_qs} Triple Qs</span>
              </div>
            </div>
          </div>
        )}

        {/* RMC - Rally Master Championship (Points) */}
        {ukcRallyData.rmc && (
          <div className="levelRow championshipRow">
            <div className="levelHeader">
              <h4>
                <strong>RMC</strong> (Masters Championship Points)
                {ukcRallyData.rmc.title_earned && <span className="earnedCheck"> ✓</span>}
              </h4>
            </div>
            <div className="levelStats">
              <div className="statItem">
                <span className="statLabel">Total: {ukcRallyData.rmc.total_points}/{ukcRallyData.rmc.required_points}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">L2B: {ukcRallyData.rmc.level_2b_points}/{ukcRallyData.rmc.required_level_2b_points}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">L3B: {ukcRallyData.rmc.level_3b_points}/{ukcRallyData.rmc.required_level_3b_points}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">ROM-B: {ukcRallyData.rmc.masters_b_points}/{ukcRallyData.rmc.required_masters_b_points}</span>
              </div>
            </div>
          </div>
        )}

        {/* RMG - Rally Master Grand Champion */}
        {ukcRallyData.rmg && (
          <div className="levelRow championshipRow">
            <div className="levelHeader">
              <h4>
                <strong>RMG</strong> (Masters Grand Championship)
                {ukcRallyData.rmg.title_earned && <span className="earnedCheck"> ✓</span>}
              </h4>
              <p className="subtitle">15 Triple Qs with ≥96 in each class</p>
            </div>
            <div className="levelStats">
              <div className="statItem">
                <span className="statLabel">Qualifying: {ukcRallyData.rmg.qualifying_triple_qs}/{ukcRallyData.rmg.required_triple_qs}</span>
              </div>
              <div className="statItem">
                <span className="statLabel">Total Triple Qs: {ukcRallyData.rmg.triple_qs}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Runs Link */}
      {/* View Run History Link - Always show, even with 0 runs */}
      {onRunUpdated ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={runHistoryModal.toggle}
          >
            View & Edit Run History ({ukcRallyData.total_runs || 0} runs)
          </button>
        </div>
      ) : ukcRallyData.recent_runs && ukcRallyData.recent_runs.length > 0 ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={runHistoryModal.toggle}
          >
            View Run History ({ukcRallyData.total_runs || 0} runs)
          </button>
        </div>
      ) : null}

      {/* Run History Modal */}
      {runHistoryModal.isShown && (
        <Modal
          isShown={runHistoryModal.isShown}
          hide={handleCloseRunsModal}
          className="runsHistoryModal"
          modalContent={
            <div className="runsModal">
              <h2>UKC Rally - Run History</h2>

              <div className="runsModalContent">
                <div className="runsSection">
                  <div className="runsSectionHeader">
                    <h4>All Runs</h4>
                  </div>
                  {ukcRallyData.recent_runs.length === 0 ? (
                    <div className="noRuns">
                      <p>No runs recorded yet.</p>
                    </div>
                  ) : (
                    <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
                      <AgGridReact
                        rowData={localRuns}
                        columnDefs={columnDefs}
                        domLayout='normal'
                        suppressCellFocus={true}
                        theme={themeQuartz}
                        getRowId={(params) => params.data._id}
                      />
                    </div>
                  )}
                </div>

                <div className="modalActions">
                  <Button onClick={handleAddRun}>
                    + Add Manual Run
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      )}

      {/* Manual Run Entry Modal */}
      {manualRunModal.isShown && (
        <ManualRunEntryModal
          isOpen={manualRunModal.isShown}
          onClose={manualRunModal.toggle}
          dog={dog}
          sportType={Constants.show_type.ukc_rally}
          sanctioningClub={Constants.sanctioning_club.UKC}
          onSuccess={handleRunSaved}
          editRun={editingRun as any}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.isShown && (
        <Modal
          isShown={deleteConfirmModal.isShown}
          hide={deleteConfirmModal.toggle}
          className="deleteConfirmModal"
          modalContent={
            <div className="deleteConfirmContent">
              <h2>Delete Run?</h2>
              <p>Are you sure you want to delete this run? This action cannot be undone.</p>
              <div className="deleteConfirmActions">
                <Button variant="secondary" onClick={deleteConfirmModal.toggle}>
                  Cancel
                </Button>
                <Button variant="default" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default UKCRallyCard;

